import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ClientInfoForm = ({ formData, onChange, errors }) => {
  const [existingClients, setExistingClients] = useState([]);
  const [showAddClientForm, setShowAddClientForm] = useState(false);

  // Mock existing clients data (replace with actual API call)
  useEffect(() => {
    // Simulate fetching existing clients
    const mockClients = [
      { 
        id: '1', 
        name: 'Sarah Johnson', 
        email: 'sarah.johnson@email.com',
        phone: '(555) 123-4567',
        address: {
          street: '123 Oak Avenue',
          city: 'New York',
          state: 'NY',
          zip: '10001'
        }
      },
      { 
        id: '2', 
        name: 'Michael Chen', 
        email: 'michael.chen@email.com',
        phone: '(555) 987-6543',
        address: {
          street: '456 Pine Street',
          city: 'Los Angeles', 
          state: 'CA',
          zip: '90210'
        }
      },
      { 
        id: '3', 
        name: 'Emily Rodriguez', 
        email: 'emily.rodriguez@email.com',
        phone: '(555) 456-7890',
        address: {
          street: '789 Maple Drive',
          city: 'Chicago',
          state: 'IL',
          zip: '60601'
        }
      }
    ];
    setExistingClients(mockClients);
  }, []);

  const handleInputChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleClientSelection = (clientId) => {
    console.log('Client selected:', clientId); // Debug log
    
    if (clientId === 'new') {
      // Switching to new client mode
      handleInputChange('isNewClient', true);
      handleInputChange('clientId', '');
      setShowAddClientForm(true);
      // Clear existing client data when switching to new client
      handleInputChange('clientName', '');
      handleInputChange('clientEmail', '');
      handleInputChange('clientPhone', '');
      handleInputChange('streetAddress', '');
      handleInputChange('city', '');
      handleInputChange('state', '');
      handleInputChange('zipCode', '');
    } else if (clientId && clientId !== '') {
      // Selecting an existing client
      const selectedClient = existingClients?.find(client => client?.id === clientId);
      console.log('Found client:', selectedClient); // Debug log
      
      if (selectedClient) {
        // Update form data with selected client info
        onChange({
          ...formData,
          isNewClient: false,
          clientId: clientId,
          clientName: selectedClient?.name,
          clientEmail: selectedClient?.email,
          clientPhone: selectedClient?.phone,
          streetAddress: selectedClient?.address?.street,
          city: selectedClient?.address?.city,
          state: selectedClient?.address?.state,
          zipCode: selectedClient?.address?.zip
        });
        setShowAddClientForm(false);
      }
    } else {
      // No selection or empty selection
      handleInputChange('isNewClient', true);
      handleInputChange('clientId', '');
      setShowAddClientForm(false);
    }
  };

  // Phone number formatting
  const handlePhoneChange = (e) => {
    let value = e?.target?.value?.replace(/\D/g, '');
    if (value?.length >= 6) {
      value = `(${value?.slice(0, 3)}) ${value?.slice(3, 6)}-${value?.slice(6, 10)}`;
    } else if (value?.length >= 3) {
      value = `(${value?.slice(0, 3)}) ${value?.slice(3)}`;
    }
    handleInputChange('clientPhone', value);
  };

  // ZIP code formatting
  const handleZipChange = (e) => {
    let value = e?.target?.value?.replace(/\D/g, '');
    if (value?.length > 5) {
      value = `${value?.slice(0, 5)}-${value?.slice(5, 9)}`;
    }
    handleInputChange('zipCode', value);
  };

  // USA States options
  const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' }
  ];

  const clientOptions = [
    { value: '', label: 'Select a client...' }, // Default option
    ...existingClients?.map(client => ({
      value: client?.id,
      label: `${client?.name} (${client?.email})`
    })),
    { value: 'new', label: '+ Add New Client' }
  ];

  // Determine current select value for display
  const getCurrentSelectValue = () => {
    if (formData?.isNewClient === true && !formData?.clientId) {
      return 'new';
    }
    if (formData?.clientId) {
      return formData?.clientId;
    }
    return '';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="User" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-heading text-foreground">Project Information</h3>
          <p className="text-sm text-muted-foreground">Set up your project and client details</p>
        </div>
      </div>

      {/* Project Title - Moved to Top */}
      <div className="mb-6">
        <Input
          label="Project Title"
          type="text"
          placeholder="Custom Diamond Ring Design"
          description="Internal reference name for this project"
          value={formData?.projectTitle || ''}
          onChange={(e) => handleInputChange('projectTitle', e?.target?.value)}
          error={errors?.projectTitle}
          required
          className="w-full"
        />
      </div>

      {/* Client Selection Dropdown */}
      <div className="mb-6">
        <Select
          label="Client"
          placeholder="Select existing client or add new"
          options={clientOptions}
          value={getCurrentSelectValue()}
          onChange={handleClientSelection}
          error={errors?.clientId}
          required
          searchable
          className="w-full"
        />
      </div>

      {/* Client Information Form - Show when new client is selected or editing existing */}
      {(formData?.isNewClient || showAddClientForm || formData?.clientId) && (
        <>
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input
              label="Client Name"
              type="text"
              placeholder="Enter client's full name"
              value={formData?.clientName || ''}
              onChange={(e) => handleInputChange('clientName', e?.target?.value)}
              error={errors?.clientName}
              required={formData?.isNewClient}
              disabled={!formData?.isNewClient}
              className="w-full"
            />

            <Input
              label="Client Email"
              type="email"
              placeholder="client@example.com"
              description="Preview link will be sent to this email"
              value={formData?.clientEmail || ''}
              onChange={(e) => handleInputChange('clientEmail', e?.target?.value)}
              error={errors?.clientEmail}
              required={formData?.isNewClient}
              disabled={!formData?.isNewClient}
              className="w-full"
            />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData?.clientPhone || ''}
              onChange={handlePhoneChange}
              error={errors?.clientPhone}
              required={formData?.isNewClient}
              disabled={!formData?.isNewClient}
              maxLength={14}
              className="w-full"
            />
          </div>

          {/* Address Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
              <h4 className="text-sm font-medium text-foreground">Client Address (USA)</h4>
              {!formData?.isNewClient && (
                <Button
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAddClientForm(true)}
                  iconName="Edit2"
                >
                  Edit Address
                </Button>
              )}
            </div>

            <Input
              label="Street Address"
              type="text"
              placeholder="123 Main Street, Apt 4B"
              value={formData?.streetAddress || ''}
              onChange={(e) => handleInputChange('streetAddress', e?.target?.value)}
              error={errors?.streetAddress}
              required={formData?.isNewClient}
              disabled={!formData?.isNewClient && !showAddClientForm}
              className="w-full"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="City"
                type="text"
                placeholder="New York"
                value={formData?.city || ''}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                error={errors?.city}
                required={formData?.isNewClient}
                disabled={!formData?.isNewClient && !showAddClientForm}
                className="w-full"
              />

              <Select
                label="State"
                placeholder="Select state"
                options={stateOptions}
                value={formData?.state || ''}
                onChange={(value) => handleInputChange('state', value)}
                error={errors?.state}
                required={formData?.isNewClient}
                disabled={!formData?.isNewClient && !showAddClientForm}
                searchable
                className="w-full"
              />

              <Input
                label="ZIP Code"
                type="text"
                placeholder="12345 or 12345-6789"
                value={formData?.zipCode || ''}
                onChange={handleZipChange}
                error={errors?.zipCode}
                required={formData?.isNewClient}
                disabled={!formData?.isNewClient && !showAddClientForm}
                maxLength={10}
                className="w-full"
              />
            </div>

            {showAddClientForm && !formData?.isNewClient && (
              <div className="flex justify-end space-x-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAddClientForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => setShowAddClientForm(false)}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Help Text */}
      {formData?.isNewClient && (
        <div className="mt-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="var(--color-accent)" />
            <p className="text-sm text-accent">
              This client will be saved to your contacts for future projects after creation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientInfoForm;